import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, HashRouter } from 'react-router-dom';
import { LEGACY_BASE, resolveShell } from './routing';
import { isRetriableError } from './api/client';
import './styles.css';

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // Retry only what a second attempt could change. `retry: 1` retried
      // everything, so a 404 on an empty project and a 402 from the plan gate
      // each took two round trips to reach the screen that explains them,
      // while a genuinely transient 5xx got only one extra chance. Two, for
      // the errors worth repeating: one edge blip runs ~3% of requests
      // (#4b), and client.ts already absorbs the pre-origin ones.
      retry: (failureCount, error) => failureCount < 2 && isRetriableError(error),
    },
  },
});

// When embedded inside VistraPM (?embed=true), use HashRouter to avoid clashing with the host's
// router and to keep our routing self-contained inside the iframe.
const isEmbedded = new URLSearchParams(window.location.search).get('embed') === 'true';
const Router = isEmbedded ? HashRouter : BrowserRouter;

/**
 * CUTOVER. The reimagined shell owns `/`; the legacy shell is preserved, not
 * deleted, at `/legacy`.
 *
 * The two are separate router roots rather than two branches of one <Routes>,
 * because the legacy shell is mounted with `basename="/legacy"` — and a
 * basename belongs to the router. That single line is what lets App.tsx move
 * without being edited: its thirty absolute routes and every internal
 * `<NavLink to="/schedule/overview">` resolve under the basename unchanged, so
 * the fallback the owner asked us to keep is the same code that was running
 * before, not a port of it.
 *
 * Which shell answers is decided once, here, before React mounts. See
 * routing.ts — the decision is a pure function so the redirect table is
 * testable, and a wrong entry in it sends people somewhere plausible rather
 * than somewhere broken, which is the failure mode worth testing for.
 */
const routedPath = isEmbedded
  // Under HashRouter the pathname is the host page's; ours lives in the hash.
  ? (window.location.hash.replace(/^#/, '').split('?')[0] || '/')
  : window.location.pathname;
const choice = resolveShell(routedPath);

if (choice.shell === 'redirect') {
  // A full navigation, not history.replaceState: the shells are separate router
  // roots, so the decision above has to be re-made against the new URL.
  if (isEmbedded) window.location.hash = `#${choice.to}`;
  else window.location.replace(choice.to + window.location.search + window.location.hash);
} else {
  const root = ReactDOM.createRoot(document.getElementById('root')!);
  const wrap = (children: React.ReactNode) => (
    <React.StrictMode>
      <QueryClientProvider client={qc}>{children}</QueryClientProvider>
    </React.StrictMode>
  );

  // Each shell is imported dynamically, so only the one being rendered is
  // fetched. Before the cutover both were in the entry chunk because one
  // <Routes> referenced both; now the choice is made before React mounts, and
  // the legacy shell — thirty screens most people will never open — is a
  // separate chunk that a visit to `/` does not pay for.
  //
  // No React.lazy or Suspense: the decision is already settled by this point,
  // so there is nothing to suspend ON, and a fallback would only flash.
  if (choice.shell === 'legacy') {
    void import('./App').then(({ default: App }) => {
      root.render(wrap(
        // basename is why App.tsx needed no edit: its absolute routes and
        // internal links resolve under /legacy unchanged.
        <Router basename={LEGACY_BASE}><App embedded={isEmbedded} /></Router>,
      ));
    });
  } else {
    void import('./newapp/NewApp').then(({ default: NewApp }) => {
      root.render(wrap(<Router><NewApp /></Router>));
    });
  }
}
